import { useMemo, useState } from 'react'
import { Filter, Loader2, Search } from 'lucide-react'
import { activeFilterCount } from '@/screens/scan/scanCache'
import { matchesFilters } from '@/screens/scan/lib/filters'
import type { UseChecklistScan } from '@/screens/scan/useChecklistScan'
import { FiltersPanel } from './FiltersPanel'
import { SelectItem } from './SelectItem'

/** Item picker: search + filter the checklist items, then dispatch a scan. */
export function SelectView({ scan }: { scan: UseChecklistScan }) {
  const [showFilters, setShowFilters] = useState(false)

  const filtered = useMemo(() => {
    const q = scan.query.trim().toLowerCase()
    return scan.items.filter((item) => {
      if (q && !`${item.name} ${item.topic ?? ''} ${item.description ?? ''}`.toLowerCase().includes(q)) {
        return false
      }
      return matchesFilters(item, scan.filters)
    })
  }, [scan.items, scan.query, scan.filters])

  const selectedCount = scan.selectedIds.size
  const filterCount = activeFilterCount(scan.filters)

  if (showFilters) {
    return <FiltersPanel scan={scan} onClose={() => setShowFilters(false)} />
  }

  return (
    <>
      <div className='ramp-scan-head'>
        <div className='ramp-scan-title'>Checklist items</div>
        <div className='ramp-scan-search-row'>
          <div className='ramp-scan-search'>
            <Search size={14} className='ramp-scan-search-icon' />
            <input
              type='text'
              placeholder='Search checklist items…'
              value={scan.query}
              onChange={(event) => scan.setQuery(event.target.value)}
            />
          </div>
          <button
            type='button'
            className={`ramp-scan-filter-btn${filterCount ? ' has-active' : ''}`}
            onClick={() => setShowFilters(true)}
            title='Filters'
            aria-label='Filters'
          >
            <Filter size={15} />
            {filterCount ? <span className='ramp-scan-filter-count'>{filterCount}</span> : null}
          </button>
        </div>
      </div>

      <div className='ramp-scan-list'>
        {scan.loadingItems ? (
          <div className='ramp-scan-empty'>
            <Loader2 size={16} className='ramp-spin' /> Loading checklist items…
          </div>
        ) : scan.itemsError ? (
          <div className='ramp-scan-empty ramp-scan-error'>
            {scan.itemsError}
            <button type='button' className='ramp-link' onClick={scan.reloadItems}>
              Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className='ramp-scan-empty'>No checklist items found.</div>
        ) : (
          filtered.map((item) => (
            <SelectItem
              key={item._id}
              item={item}
              checked={scan.selectedIds.has(item._id)}
              onToggle={() => scan.toggleSelect(item._id)}
            />
          ))
        )}
      </div>

      <div className='ramp-scan-foot'>
        {scan.scanError ? <div className='ramp-scan-error'>{scan.scanError}</div> : null}
        <button
          type='button'
          className='ramp-scan-primary'
          disabled={selectedCount === 0 || scan.scanning}
          onClick={scan.runScan}
        >
          {scan.scanning ? (
            <>
              <Loader2 size={16} className='ramp-spin' /> Scanning…
            </>
          ) : (
            `Scan ${selectedCount || ''} item${selectedCount === 1 ? '' : 's'}`.trim()
          )}
        </button>
      </div>
    </>
  )
}
